import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';
import { parentsAPI } from '../utils/api';
import { tField } from '../utils/bilingualHelper';

export default function Parents() {
  const { t } = useTranslation();
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetchIdRef = React.useRef(0);

  const load = (isMounted = true) => {
    const currentId = ++fetchIdRef.current;
    parentsAPI.getAll()
      .then(r => { if (isMounted && currentId === fetchIdRef.current) setParents(r.data); })
      .catch(err => { if (isMounted && currentId === fetchIdRef.current) console.error(err); })
      .finally(() => { if (isMounted && currentId === fetchIdRef.current) setLoading(false); });
  };

  useEffect(() => {
    let isMounted = true;
    load(isMounted);
    return () => { isMounted = false; };
  }, []);

  const toggleStatus = async (p) => {
    const newStatus = p.status === 'active' ? 'inactive' : 'active';
    try {
      await parentsAPI.toggleStatus(p._id, newStatus);
      setParents(prev => prev.map(x => x._id === p._id ? { ...x, status: newStatus } : x));
    } catch { alert(t('parents.statusFailed')); }
  };

  const filtered = parents.filter(p => 
    tField(p.name).toLowerCase().includes(filter.toLowerCase()) || 
    p.mobile?.includes(filter)
  );

  return (
    <Layout title={t('parents.title')}>
      <div className="dash-page-header">
        <div>
          <h1 className="dash-page-title">{t('parents.headerTitle')}</h1>
          <p className="text-sm text-muted">{t('parents.headerSub')}</p>
        </div>
        <div style={{ display:'flex', gap:12 }}>
          <input 
            type="text" placeholder={t('parents.search')} className="form-input-style" 
            style={{ width:260 }} value={filter} onChange={e => setFilter(e.target.value)}
          />
        </div>
      </div>

      {loading ? <div className="p-8">⏳ {t('parents.syncing')}</div> : (
        <div className="dash-card p-0" style={{ overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f9f9f9', borderBottom:'1px solid var(--border)' }}>
                <th style={{ padding:16, textAlign:'left' }}>{t('parents.thGuardian')}</th>
                <th style={{ padding:16, textAlign:'left' }}>{t('parents.thContact')}</th>
                <th style={{ padding:16, textAlign:'left' }}>{t('parents.thHousehold')}</th>
                <th style={{ padding:16, textAlign:'right' }}>{t('parents.thControls')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p._id} style={{ borderBottom:'1px solid var(--border)' }}>
                  <td style={{ padding:16 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div className="dash-avatar" style={{ width:32, height:32, background:'var(--wine)', color:'#fff' }}>{(tField(p.name) || 'P')[0]}</div>
                      <div>
                        <div className="font-bold">{tField(p.name)}</div>
                        <div className="text-xs text-muted">Role: {p.status === 'active' ? t('parents.roleActive') : t('parents.roleInactive')}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding:16 }}>
                    <div className="text-sm">{p.mobile || t('parents.noMobile')}</div>
                    <div className="text-xs text-muted">{p.email}</div>
                  </td>
                  <td style={{ padding:16 }}>
                    <div className="text-sm font-bold">{p.householdMembers?.length || 0} {t('parents.students')}</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:6 }}>
                      {p.householdMembers?.map(m => (
                        <button 
                          key={m.studentId} 
                          className="badge badge-outline" 
                          style={{ cursor:'pointer', fontSize:10 }}
                          onClick={() => window.location.href = `/students/${m.studentId}`}
                        >
                          👤 {tField(m.name)}
                        </button>
                      ))}
                    </div>
                    <div className="text-xs text-muted mt-2">{t('parents.emergency')} {p.emergencyContact || t('parents.none')}</div>
                  </td>
                  <td style={{ padding:16, textAlign:'right' }}>
                    <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                      <button 
                        className={`btn btn-xs ${p.status === 'active' ? 'btn-outline border-danger text-danger' : 'btn-primary'}`}
                        onClick={() => toggleStatus(p)}
                      >
                        {p.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!filtered.length && (
            <div className="p-12 text-center text-muted">{t('parents.noParents')}</div>
          )}
        </div>
      )}
    </Layout>
  );
}
